import { Injectable } from '@angular/core';

interface CacheEntry {
  expiresAt: number;
  value: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class ApiCacheService {
  private readonly storageKey = 'dealszo_api_cache';
  private readonly defaultTtlMs = 5 * 60 * 1000;

  private getStore(): Storage | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    return sessionStorage;
  }

  get(key: string): unknown | null {
    const store = this.getStore();
    if (!store) {
      return null;
    }

    const entry = this.readEntry(store, key);
    if (!entry) {
      return null;
    }

    if (Date.now() > entry.expiresAt) {
      this.deleteFromStore(store, key);
      return null;
    }

    return entry.value;
  }

  set(key: string, value: unknown, ttlMs: number = this.defaultTtlMs): void {
    const store = this.getStore();
    if (!store) {
      return;
    }

    const cache = this.readAll(store);
    cache[key] = {
      expiresAt: Date.now() + ttlMs,
      value,
    };

    store.setItem(this.storageKey, JSON.stringify(cache));
  }

  clear(key: string): void {
    const store = this.getStore();
    if (!store) {
      return;
    }

    this.deleteFromStore(store, key);
  }

  clearAll(): void {
    const store = this.getStore();
    if (!store) {
      return;
    }

    store.removeItem(this.storageKey);
  }

  private readAll(store: Storage): Record<string, CacheEntry> {
    const raw = store.getItem(this.storageKey);
    if (!raw) {
      return {};
    }

    try {
      const parsed = JSON.parse(raw) as Record<string, CacheEntry>;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private readEntry(store: Storage, key: string): CacheEntry | null {
    const all = this.readAll(store);
    const entry = all[key];
    return entry ?? null;
  }

  private deleteFromStore(store: Storage, key: string): void {
    const all = this.readAll(store);
    delete all[key];
    store.setItem(this.storageKey, JSON.stringify(all));
  }
}
